import type { CSSProperties } from 'react'
import { Link } from '@tanstack/react-router'

export interface CompositeRequestBlockProps {
  siblingId: string
  thisTypeLabel: string
  thisDays: number
  siblingTypeLabel: string
  siblingDays: number
  totalDays: number
}

/**
 * Header-level composite-request indicator surfaced under the status
 * chip whenever a leave has `linked_request_id` populated. Rendered
 * identically on both legs of an Annual/Medical/etc. + Unpaid pair so a
 * manager opening either side sees the full request at a glance.
 *
 * Pure layout — no business logic; the parent passes a fully resolved
 * payload from `LeaveDetailPayload.composite`.
 */
export function CompositeRequestBlock({
  siblingId,
  thisTypeLabel,
  thisDays,
  siblingTypeLabel,
  siblingDays,
  totalDays,
}: CompositeRequestBlockProps): JSX.Element {
  return (
    <div
      data-testid="composite-request-block"
      style={containerStyle}
    >
      <div style={badgeRowStyle}>
        <span style={badgeStyle}>Composite request</span>
        <span style={totalStyle}>
          {formatDays(totalDays)} day{totalDays === 1 ? '' : 's'} total
        </span>
      </div>
      <div style={breakdownStyle}>
        <BreakdownPill label={thisTypeLabel} value={formatDays(thisDays)} accent="terracotta" />
        <span style={connectorStyle} aria-hidden="true">+</span>
        <BreakdownPill label={siblingTypeLabel} value={formatDays(siblingDays)} accent="ink-3" />
      </div>
      <div style={linkRowStyle}>
        <Link
          to="/leave"
          search={{ id: siblingId }}
          data-testid="composite-sibling-link"
          style={linkStyle}
        >
          View the {siblingTypeLabel} leg →
        </Link>
        <span style={helperStyle}>
          Director must approve the full composite as one cascade.
        </span>
      </div>
    </div>
  )
}

function formatDays(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1)
}

interface BreakdownPillProps {
  label: string
  value: string
  accent: 'terracotta' | 'ink-3'
}

function BreakdownPill({
  label,
  value,
  accent,
}: BreakdownPillProps): JSX.Element {
  const color = accent === 'terracotta' ? 'var(--steel-blue)' : 'var(--text-3)'
  return (
    <div style={breakdownPillStyle}>
      <span
        style={{
          display: 'inline-block',
          width: 8,
          height: 8,
          borderRadius: 999,
          background: color,
        }}
        aria-hidden="true"
      />
      <span style={{ fontWeight: 600, color: 'var(--text)' }}>{value}</span>
      <span style={{ color: 'var(--text-3)', fontSize: 12 }}>{label}</span>
    </div>
  )
}

// ── styles ────────────────────────────────────────────────────────────────────

const containerStyle: CSSProperties = {
  marginTop: 8,
  padding: '10px 12px',
  background: 'var(--surface-2)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
}

const badgeRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  flexWrap: 'wrap',
}

const badgeStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'var(--steel-blue)',
  background: 'var(--warning-bg)',
  padding: '2px 8px',
  borderRadius: 999,
}

const totalStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-3)',
}

const breakdownStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
}

const breakdownPillStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  fontSize: 14,
}

const connectorStyle: CSSProperties = {
  fontSize: 14,
  fontWeight: 600,
  color: 'var(--text-3)',
}

const linkRowStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 10,
  flexWrap: 'wrap',
}

const linkStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--steel-blue)',
  textDecoration: 'none',
  fontWeight: 600,
}

const helperStyle: CSSProperties = {
  fontSize: 12,
  color: 'var(--text-3)',
  fontStyle: 'italic',
}
